import { Stack } from '@mui/material';
import {
  ComponentProps,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useRef,
} from 'react';

import { Button, ButtonProps } from '@/components/form/baseInputs/Button';
import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { COLORS } from '@/styles/tokens/colors';

export type ChatComposerInputProps = ComponentProps<typeof TextInput>;

export interface ChatComposerToolbarProps {
  buttonEndIcon?: ButtonProps['endIcon'];
  buttonLabel: ReactNode;
  buttonVariant: ButtonProps['variant'];
  leadingAction?: ReactNode;
  onSubmit: () => Promise<void>;
  submitDisabled: boolean;
  testId?: string;
  trailingAction?: ReactNode;
}

export interface ChatComposerProps {
  allowEmptySubmit?: boolean;
  autoFocusOnMount?: boolean;
  buttonEndIcon?: ButtonProps['endIcon'];
  buttonLabel: ReactNode;
  buttonVariant?: ButtonProps['variant'];
  /**
   * Optional strip docked at the top of the composer box, above the text
   * area, showing the object attached to the next message (see
   * ChatComposerContextBar). Render it conditionally: pass undefined when
   * there is no attached context.
   */
  contextBar?: ReactNode;
  disabled?: boolean;
  /** Keeps the focused composer treatment visible while attached context is selected. */
  emphasized?: boolean;
  hideLabel?: boolean;
  /** Custom input slot. ChatComposer injects the controlled input behavior. */
  input?: (props: ChatComposerInputProps) => ReactNode;
  inputLabel?: string;
  /**
   * Optional control rendered at the bottom-left of the composer toolbar,
   * opposite the submit button (e.g. the household chat's answer-depth
   * selector). Surfaces without one leave the left side empty.
   */
  leadingAction?: ReactNode;
  maxRows?: number;
  /**
   * Minimum number of text rows the entry area shows before it grows. This
   * sizes the typing area itself, independent of the docked toolbar or any
   * other composer chrome. Defaults to a single row.
   */
  minRows?: number;
  onSubmit: (message: string) => Promise<void> | void;
  onValueChange: (value: string) => void;
  placeholder: string;
  submitDisabled?: boolean;
  /**
   * Base test id for the composer's interactive elements: the text area gets
   * `${testId}-input` and the submit button `${testId}-submit`.
   */
  testId?: string;
  /** Custom toolbar slot. ChatComposer injects the submit behavior. */
  toolbar?: (props: ChatComposerToolbarProps) => ReactNode;
  /**
   * Optional control rendered at the bottom-right of the composer toolbar,
   * just before the submit button (e.g. the household chat's restore-context
   * button).
   */
  trailingAction?: ReactNode;
  value: string;
}

function DefaultChatComposerToolbar({
  buttonEndIcon,
  buttonLabel,
  buttonVariant,
  leadingAction,
  onSubmit,
  submitDisabled,
  testId,
  trailingAction,
}: ChatComposerToolbarProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%', px: 1, pb: 1 }}
    >
      {leadingAction ?? <span />}
      <Stack direction="row" alignItems="center" gap={1.5}>
        {trailingAction}
        <Button
          data-testid={testId ? `${testId}-submit` : undefined}
          variant={buttonVariant}
          size="xs"
          sx={{ height: '20px' }}
          onClick={onSubmit}
          disabled={submitDisabled}
          endIcon={buttonEndIcon}
          iconProps={{ 'aria-hidden': true }}
        >
          {buttonLabel}
        </Button>
      </Stack>
    </Stack>
  );
}

function DefaultChatComposerInput(props: ChatComposerInputProps) {
  return (
    <Stack sx={{ pb: 2, pt: 1, px: 2 }}>
      <TextInput {...props} />
    </Stack>
  );
}

export function ChatComposer({
  allowEmptySubmit = false,
  autoFocusOnMount = true,
  buttonEndIcon,
  buttonLabel,
  buttonVariant = 'primary',
  contextBar,
  disabled = false,
  emphasized = false,
  hideLabel = true,
  input,
  inputLabel = 'Ask',
  leadingAction,
  maxRows = 20,
  minRows,
  onSubmit,
  onValueChange,
  placeholder,
  submitDisabled = disabled,
  testId,
  toolbar,
  trailingAction,
  value,
}: ChatComposerProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!autoFocusOnMount) {
      return;
    }

    // Run after parent overlays and focus traps to preserve the intended input focus.
    const frameId = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, [autoFocusOnMount]);

  const handleSubmit = async () => {
    if ((!allowEmptySubmit && !value.trim()) || submitDisabled) {
      return;
    }

    await onSubmit(value);
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSubmit();
    }
  };

  const toolbarProps: ChatComposerToolbarProps = {
    buttonEndIcon,
    buttonLabel,
    buttonVariant,
    leadingAction,
    onSubmit: handleSubmit,
    submitDisabled,
    testId,
    trailingAction,
  };
  const toolbarContent = toolbar ? (
    toolbar(toolbarProps)
  ) : (
    <DefaultChatComposerToolbar {...toolbarProps} />
  );

  const inputProps: ChatComposerInputProps = {
    testId: testId ? `${testId}-input` : undefined,
    placeholder,
    label: inputLabel,
    hideLabel,
    value,
    onChange: (event) => onValueChange(event.target.value),
    inputRef,
    onKeyDown: handleKeyDown,
    disabled,
    multiline: true,
    minRows,
    maxRows,
    startAdornment: contextBar,
    endAdornment: toolbarContent,
    sx: {
      // The themed OutlinedInput forces 10px/14px padding onto the textarea.
      // Keep that padding inside the input's 100% width.
      '& .MuiInputBase-input': {
        boxSizing: 'border-box',
        color: COLORS.TEXT.PRIMARY,
      },
      // Stack the textarea above the full-width toolbar so the controls dock
      // inside the input box.
      '& .MuiInputBase-root': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        boxShadow: emphasized ? (theme) => theme.palette.shadows.xs : undefined,
      },
      // Keep the focused stroke the same width as the resting stroke.
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1px',
      },
      ...(emphasized
        ? {
            '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.GRAY[500],
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              boxShadow: (theme) => theme.palette.shadows.xs,
            },
          }
        : {}),
    },
  };

  return input ? (
    input(inputProps)
  ) : (
    <DefaultChatComposerInput {...inputProps} />
  );
}
